import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Sparkles, Loader2, Plus, FileSearch, Bot } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ModelPicker } from "@/components/common/ModelPicker";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const METRICS = [["aim", "Aim"], ["tracking", "Tracking"], ["reaction", "Reaction"], ["crosshair", "Crosshair"], ["consistency", "Consistency"]];

export default function Integrity() {
    const navigate = useNavigate();
    const [encounters, setEncounters] = useState(null);
    const [anomalies, setAnomalies] = useState([]);
    const [games, setGames] = useState([]);
    const [name, setName] = useState("");
    const [game, setGame] = useState("warzone");
    const [model, setModel] = useState("gpt-5.2");
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        const [e, a, g] = await Promise.all([api.get("/encounters"), api.get("/anomalies"), api.get("/games")]);
        setEncounters(e.data.encounters);
        setAnomalies(a.data.anomalies);
        setGames(g.data.games);
    };
    useEffect(() => { load(); }, []);

    const analyze = async () => {
        if (!name.trim()) { toast.error("Enter a player name."); return; }
        setBusy(true);
        try {
            await api.post("/anomalies/analyze", { player_name: name.trim(), game, model });
            await api.post("/encounters", { player_name: name.trim(), game });
            toast.success("Behavioral analysis complete.");
            setName(""); load();
        } catch (error) { toast.error(error?.response?.data?.detail || "Analysis failed."); }
        finally { setBusy(false); }
    };

    const createEvidence = async (a) => {
        setCreating(true);
        try {
            await api.post("/evidence", { anomaly_id: a.id, title: `Encounter Review — ${a.player_name}` });
            toast.success("Evidence package created.");
            navigate("/evidence");
        } catch { toast.error("Failed to create package."); }
        finally { setCreating(false); }
    };

    if (!encounters) return <Loader label="Loading integrity center" />;

    return (
        <div className="ww-fade-up">
            <PageHeader title="Encounters & Anomaly" subtitle="Track repeat opponents and run fair behavioral analysis on visible gameplay." />

            <div className="bg-surface border border-border rounded-sm p-5 mb-6">
                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                    <div className="flex-1">
                        <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Analyze Player (visible name)</label>
                        <Input data-testid="anomaly-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. QuickScope99"
                            className="bg-obsidian border-border rounded-sm h-10 mt-1.5" />
                    </div>
                    <Select value={game} onValueChange={setGame}>
                        <SelectTrigger className="w-full md:w-[160px] h-10 bg-obsidian border-border rounded-sm" data-testid="anomaly-game"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-surface2 border-border">{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <ModelPicker value={model} onChange={setModel} />
                    <button data-testid="run-anomaly" onClick={analyze} disabled={busy}
                        className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyze
                    </button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5">
                    <ShieldAlert className="w-3.5 h-3.5 text-warn" /> WhiteWater AI never labels players as confirmed cheaters — only flags behavior for review.
                </p>
            </div>

            <Tabs defaultValue="anomaly">
                <TabsList className="bg-obsidian border border-border rounded-sm h-10 mb-4">
                    <TabsTrigger value="anomaly" data-testid="tab-anomaly" className="data-[state=active]:bg-volt data-[state=active]:text-black rounded-sm">Anomaly Analysis</TabsTrigger>
                    <TabsTrigger value="encounters" data-testid="tab-encounters" className="data-[state=active]:bg-volt data-[state=active]:text-black rounded-sm">Encounter Tracking</TabsTrigger>
                </TabsList>

                <TabsContent value="anomaly">
                    <div className="bg-surface border border-border rounded-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                    <th className="text-left font-semibold px-4 py-3">Player</th>
                                    <th className="text-left font-semibold px-4 py-3">Bot Class</th>
                                    <th className="text-left font-semibold px-4 py-3">Behavior</th>
                                    <th className="text-right font-semibold px-4 py-3 font-mono hidden sm:table-cell">Obs</th>
                                    <th className="text-right font-semibold px-4 py-3 font-mono">Confidence</th>
                                    <th className="text-right font-semibold px-4 py-3">Action</th>
                                </tr></thead>
                                <tbody>
                                    {anomalies.map((a) => (
                                        <tr key={a.id} className="border-b border-border/50 hover:bg-white/[0.03] cursor-pointer" onClick={() => setActive(a)} data-testid={`anomaly-row-${a.id}`}>
                                            <td className="px-4 py-3"><div className="text-white font-mono">{a.player_name}</div><div className="text-xs text-muted-foreground capitalize">{a.game}</div></td>
                                            <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Bot className="w-3.5 h-3.5" />{a.classification}</span></td>
                                            <td className="px-4 py-3"><StatusBadge status={a.behavior_status} /></td>
                                            <td className="px-4 py-3 text-right font-mono text-muted-foreground hidden sm:table-cell" data-testid={`anomaly-obs-${a.id}`}>{a.observations ?? "—"}</td>
                                            <td className="px-4 py-3 text-right font-mono text-white">{a.confidence}%</td>
                                            <td className="px-4 py-3 text-right">
                                                <button onClick={(e) => { e.stopPropagation(); createEvidence(a); }} disabled={creating}
                                                    className="text-xs text-volt hover:underline inline-flex items-center gap-1" data-testid={`make-evidence-${a.id}`}>
                                                    <FileSearch className="w-3.5 h-3.5" /> Evidence
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="encounters">
                    <div className="bg-surface border border-border rounded-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                    <th className="text-left font-semibold px-4 py-3">Player Name</th>
                                    <th className="text-left font-semibold px-4 py-3">Game</th>
                                    <th className="text-right font-semibold px-4 py-3 font-mono">Encounters</th>
                                    <th className="text-right font-semibold px-4 py-3 hidden sm:table-cell">Last Seen</th>
                                </tr></thead>
                                <tbody>
                                    {encounters.map((e) => (
                                        <tr key={e.id} className="border-b border-border/50 hover:bg-white/[0.03]" data-testid={`encounter-row-${e.id}`}>
                                            <td className="px-4 py-3 text-white font-mono">{e.player_name}</td>
                                            <td className="px-4 py-3 text-muted-foreground capitalize">{e.game}</td>
                                            <td className="px-4 py-3 text-right font-mono text-volt">{e.encounter_count}</td>
                                            <td className="px-4 py-3 text-right text-muted-foreground font-mono text-xs hidden sm:table-cell">{String(e.last_seen).slice(0, 10)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="bg-surface border-border max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-mono">{active?.player_name}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Self-learning behavioral profile{active?.observations ? ` · ${active.observations} observation${active.observations === 1 ? "" : "s"}` : ""}
                        </DialogDescription>
                    </DialogHeader>
                    {active && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <StatusBadge status={active.behavior_status} />
                                <span className="text-xs text-muted-foreground">{active.classification} · {active.confidence}% confidence</span>
                            </div>
                            {typeof active.signal === "number" && (
                                <div data-testid="anomaly-signal">
                                    <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Anomaly Signal</span><span className="font-mono text-white">{Math.round(active.signal * 100)}%</span></div>
                                    <div className="h-1.5 bg-obsidian rounded-full overflow-hidden"><div className="h-full bg-warn rounded-full" style={{ width: `${Math.round(active.signal * 100)}%` }} /></div>
                                </div>
                            )}
                            <div className="space-y-3">
                                {METRICS.map(([k, label]) => (
                                    <div key={k}>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="text-muted-foreground">{label}</span>
                                            <span className="font-mono text-white">{active.metrics?.[k]}{active.metrics_std?.[k] != null ? <span className="text-muted-foreground"> ±{active.metrics_std[k]}</span> : null}</span>
                                        </div>
                                        <div className="h-1.5 bg-obsidian rounded-full overflow-hidden"><div className="h-full bg-volt rounded-full" style={{ width: `${active.metrics?.[k]}%` }} /></div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm text-muted-foreground border-t border-border pt-3">{active.notes}</p>
                            <button onClick={() => createEvidence(active)} disabled={creating}
                                className="w-full h-10 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center justify-center gap-2">
                                <Plus className="w-4 h-4" /> Create Evidence Package
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
